import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, LinearProgress, makeStyles } from "@material-ui/core";
import * as _ from 'lodash';

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
// import { Edit } from "@material-ui/icons";
import { Link, useHistory } from "react-router-dom";

import AppUtil from "../../../../utilities/AppUtil";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import InputSearch from "../../../../components/Input/InputSearch";
import AppConfirmDialog from "../../../../components/Dialog/AppConfirmDialog";
import AppTable from "../../../Admin/Component/Table/AppTable";
import TablePaginationActions from "../../../Admin/Component/Table/TablePaginationActions";
import { getAuthInfo } from "../../../../store/slices/auth";
import { fetchAdminUserListPaging, reduceDeleteAdminUser } from "../../../../store/slices/admin";
import { SYSTEM_ROLE } from "config/AppConfig";
import { reduceDeleteCompanyUser } from "store/slices/companyUser";
import { fetchCompanyUserListPaging } from "store/slices/companyUser";
import { useMedia } from "react-use";
import AppTableMobile from "../Table/AppTableMobile";

const headCells = [
    {
        id: "firstName",
        numeric: "left",
        disablePadding: false,
        label: "label.first_name",
        sort: true,
    },
    {
        id: "lastName",
        numeric: "left",
        disablePadding: false,
        label: "label.last_name",
        sort: true,
    },
    {
        id: "fullName",
        numeric: "left",
        disablePadding: false,
        label: "label.full_name",
        sort: false,
    },
    {
        id: "email",
        numeric: "left",
        disablePadding: false,
        label: "label.email",
        sort: true,
    },
    {
        id: "actions",
        numeric: "center",
        disablePadding: false,
        label: "label.actions",
        sort: false,
    },
];

const styles = makeStyles((theme) => ({
    shortText: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        maxWidth: '376px'
    },
    customTableFooter: {
        flexShrink: 0,
        marginLeft: theme.spacing(2.5),
        '& .MuiToolbar-root': {
            '& .MuiTablePagination-spacer': {
                // display: 'none'
            }
        },
        "& .MuiTablePagination-root": {
            // overflow: 'visible'
        }
    },
}));

const STYLE = {
    labelFromToDate: {
        display: "flex",
        alignItems: 'flex-start',
        justifyContent: "flex-end",
        minWidth: '80px',
        margin: '0.3rem 5px 0 0',
        fontWeight: 'bold'
    },
    hyperLink: {
        color: AppColor.blue,
        textDecoration: 'underline'
    }
}

const AdminUserTable = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = styles();
    const below950 = useMedia('(max-width: 950px)');
    const isMobile = AppUtil.mobileAndTabletCheck();
    const [queries, setQueries] = useState({
        sort: true,
        page_number: 1,
        page_size: isMobile ? 25 : 10,
        search_key: "",
        sort_field: "name",
    });
    // const userList = useSelector(getUserCoordinatorList);
    const user = useSelector(getAuthInfo);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [data, setData] = useState([]);
    const [selected, setSelected] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [searchKey, setSearchKey] = useState("");

    const emptyRows = data.length === 0;

    const _handleDeleteClients = async () => {
        if (selected.length > 0) {
            setDeleting(true);
            if (user.role === SYSTEM_ROLE.COMPANY) {
                await dispatch(reduceDeleteCompanyUser({ companyId: user.companyId, ids: selected }));
            } else {
                await dispatch(reduceDeleteAdminUser(selected));
            }
            setShowDeleteModal(false);
            setDeleting(false);
            if ((data.length === 1 || selected.length === data.length) && queries.page_number > 1) {
                _loadData({ ...queries, page_number: queries.page_number - 1 });
            } else {
                if (queries.search_key) {
                    _loadData({ ...queries, search_key: queries.search_key });
                } else {
                    _loadData();
                }
            }
            setSelected([]);
        }
    }

    const _handleSelect = (id) => {
        if (id === user.id) {
            return;
        }
        if (!selected.includes(id)) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter(item => item !== id))
        }
    }

    const _handleSelectAll = () => {
        if (data.filter(item => item.id !== user.id).length === selected.length) {
            setSelected([]);
        } else {
            setSelected(_.union(selected, data.filter(item => item.id !== user.id).map(item => item.id)));
        }
    }

    const handleChangePage = (event, newPage) => {
        _loadData({ ...queries, page_number: newPage + 1 });
        AppUtil.scrollToTopById("main-panel");
    };

    const handleChangeRowsPerPage = (event) => {
        _loadData({
            ...queries,
            page_number: 1,
            page_size: parseInt(event.target.value),
        });
        AppUtil.scrollToTopById("main-panel");
    };

    const onRequestSort = (event, property) => {
        _loadData({
            ...queries,
            sort_field: property,
            sort:
                property === queries.sort_field ? queries.sort !== true : true,
        });
    };

    const onSearch = (value) => {
        setSearchKey(value);
        _loadData({
            ...queries,
            search_key: value,
            page_number: 1,
        });
    };

    const _loadData = async ({ page_number, page_size, search_key, sort, sort_field } = {}) => {
        const newQueries = {
            ...queries,
        };
        if (page_number) {
            newQueries.page_number = page_number;
        }
        if (page_size) {
            newQueries.page_size = page_size;
        }
        if (sort_field) {
            if (sort_field === 'fullName') {
                newQueries.sort_field = 'first_name';
            } else {
                newQueries.sort_field = sort_field;
            }
        }
        newQueries.search_key = search_key || "";
        newQueries.sort = !(!sort || false);
        if (user.role === SYSTEM_ROLE.COMPANY) {
            newQueries.company_id = user.companyId;
        }
        setLoading(true);
        dispatch(user.role === SYSTEM_ROLE.COMPANY ? fetchCompanyUserListPaging(newQueries) : fetchAdminUserListPaging(newQueries))
            .then((response) => {
                setQueries(newQueries);
                setLoading(false);
                // if (response === 403) {
                //     window.location.reload();
                // } else
                if (response.payload) {
                    setData(response.payload.content);
                    setTotalElements(response.payload.totalElements);
                }
            });
    };

    useEffect(() => {
        _loadData();
    }, []);

    // useEffect(() => {
    //     if (userList)
    //         setData(userList);
    // }, [userList])

    return (
        <Box>
            <div>
                <AppButton
                    label={t('button.create')}
                    onClick={() => history.push('/admin/users/create')}
                    style={{
                        marginRight: '1rem'
                    }}
                />
                <AppButton
                    label={t('button.delete')}
                    buttonColor={AppColor.pink}
                    onClick={() => setShowDeleteModal(true)}
                    disabled={selected.length === 0 || deleting}
                />
            </div>
            <Box my={3} style={{ width: below950 ? '50%' : 'auto' }}>
                <InputSearch
                    onSearch={onSearch}
                    searchText={searchKey}
                />
            </Box>
            {!isMobile && <AppTable
                onRequestSort={onRequestSort}
                order={queries.sort === true ? "asc" : "desc"}
                orderBy={queries.sort_field}
                rowCount={data.length}
                headCells={headCells.filter(item => item && item.id !== 'fullName')}
                numSelected={selected.length}
                onSelectAllClick={_handleSelectAll}
                checkboxIndeterminate={selected.length > 0 && selected.length < data.filter(item => item.id !== user.id).length}
                checkboxChecked={data.filter(item => item.id !== user.id).length > 0 && selected.length === data.filter(item => item.id !== user.id).length}
            >
                <TableBody>
                    {loading && (<TableRow>
                        <TableCell colSpan={7} padding="none">
                            <LinearProgress />
                        </TableCell>
                    </TableRow>)}
                    {data.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell component="th" padding="checkbox">
                                {
                                    (row.id !== user.id) && (
                                        <Checkbox
                                            checked={selected.indexOf(row.id) !== -1}
                                            inputProps={{ 'aria-labelledby': row.id }}
                                            onClick={() => _handleSelect(row.id)}
                                            color="primary"
                                        />
                                    )
                                }
                            </TableCell>
                            <TableCell component="th">{row.firstName.length > 32 ?
                                <p className={classes.shortText}>{row.firstName}</p> :
                                <span>{row.firstName}</span>}</TableCell>
                            <TableCell component="th">{row.lastName.length > 32 ?
                                <p className={classes.shortText}>{row.lastName}</p> :
                                <span>{row.lastName}</span>}</TableCell>
                            <TableCell component="th">
                                {row.email.length > 100 ?
                                    <p className={classes.shortText}>{row.email}</p> :
                                    <span>{row.email}</span>}
                            </TableCell>
                            {!isMobile && <TableCell component="th" style={{ textAlign: 'center' }}>
                                {row.id === user.id ? (
                                    <Button variant="text" disabled>
                                        {/* <Edit /> */}
                                    </Button>
                                ) : (
                                    <Link
                                        to={`/admin/users/detail/${row.id}`}
                                        className="text-center"
                                    >
                                        <Button variant="text">
                                            <i className="fas fa-edit" />
                                            {/* <i className="fas fa-ellipsis-h" /> */}
                                        </Button>
                                    </Link>
                                )}
                            </TableCell>}
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 }}>
                            <TableCell colSpan={headCells.length + 1} className="text-center">
                                {t("label.no_data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            colSpan={headCells.length}
                            count={totalElements}
                            rowsPerPage={queries.page_size}
                            page={queries.page_number - 1}
                            SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelRowsPerPage={`${t("label.rows_per_page")}:`}
                            labelDisplayedRows={({ from, to, count }) =>
                                t("label.from_to", {
                                    from,
                                    to,
                                    count:
                                        count !== -1 ? count : `${t("text.more_than")} ${to}`,
                                })
                            }
                            style={{
                                overflowX: 'hidden'
                            }}
                            className={classes.customTableFooter}
                        />
                    </TableRow>
                </TableFooter>
            </AppTable>}

            {isMobile && <AppTableMobile
                onRequestSort={onRequestSort}
                order={queries.sort === true ? "asc" : "desc"}
                orderBy={queries.sort_field}
                rowCount={data.length}
                headCells={headCells.filter(item => item && item.id !== 'actions' && item.id !== 'firstName' && item.id !== 'lastName')}
                numSelected={selected.length}
                onSelectAllClick={_handleSelectAll}
                checkboxIndeterminate={selected.length > 0 && selected.length < data.filter(item => item.id !== user.id).length}
                checkboxChecked={data.filter(item => item.id !== user.id).length > 0 && selected.length === data.filter(item => item.id !== user.id).length}
                emptyRows={emptyRows}
                colSpan={headCells.length}
                count={totalElements}
                rowsPerPage={queries.page_size}
                page={queries.page_number - 1}
                minWidth={400}
                totalElements={totalElements}
                queries={queries}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                TablePaginationActions={TablePaginationActions}
            >
                <TableBody>
                    {loading && (<TableRow>
                        <TableCell colSpan={7} padding="none">
                            <LinearProgress />
                        </TableCell>
                    </TableRow>)}
                    {data.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell component="th" padding="checkbox">
                                {
                                    (row.id !== user.id) && (
                                        <Checkbox
                                            checked={selected.indexOf(row.id) !== -1}
                                            inputProps={{ 'aria-labelledby': row.id }}
                                            onClick={() => _handleSelect(row.id)}
                                            color="primary"
                                        />
                                    )
                                }
                            </TableCell>
                            <TableCell style={STYLE.hyperLink} >
                                <Link
                                    to={`/admin/users/detail/${row.id}`}
                                    className="text-left"
                                >
                                    {AppUtil.shortenText(`${row.firstName} ${row.lastName}`)}
                                </Link>
                            </TableCell>
                            <TableCell component="th">
                                {row.email.length > 100 ?
                                    <p className={classes.shortText}>{row.email}</p> :
                                    <span>{row.email}</span>}
                            </TableCell>
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 }}>
                            <TableCell colSpan={headCells.length + 1} className="text-center">
                                {t("label.no_data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                {/* <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            colSpan={headCells.length}
                            count={totalElements}
                            rowsPerPage={queries.page_size}
                            page={queries.page_number - 1}
                            SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelRowsPerPage={`${t("label.rows_per_page")}:`}
                            labelDisplayedRows={({ from, to, count }) =>
                                t("label.from_to", {
                                    from,
                                    to,
                                    count:
                                        count !== -1 ? count : `${t("text.more_than")} ${to}`,
                                })
                            }
                            style={{
                                overflowX: 'hidden'
                            }}
                            className={classes.customTableFooter}
                        />
                    </TableRow>
                </TableFooter> */}
            </AppTableMobile>}
            <AppConfirmDialog
                open={showDeleteModal}
                onConfirm={() => _handleDeleteClients()}
                onClose={() => setShowDeleteModal(false)}
                title={t('title.delete_users')}
                content={t('text.delete_users')}
            />
        </Box>
    );
};

export default AdminUserTable;